import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useMemo } from "react";
import { format, startOfMonth, endOfMonth, differenceInYears, parseISO } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Cake, ArrowLeft, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const getMemberMinistries = (department: string | null | undefined): string[] => {
  if (!department) return [];
  return department.split(",").map((value) => value.trim()).filter(Boolean);
};

const BirthdaysPage = () => {
  const { profile } = useChurch();
  const { role } = useAuth();
  const { getPeopleOrganizationIds, hasPeopleScopedAccess } = usePermissions();
  const churchId = profile?.id;
  const navigate = useNavigate();
  const now = new Date();
  const allowedPeopleOrgIds = getPeopleOrganizationIds();
  const peopleScoped = role !== "admin" && hasPeopleScopedAccess();

  const { data: members = [] } = useQuery({
    queryKey: ["birthdays_page_members", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, date_of_birth, phone, email, profile_photo_url, department")
        .eq("church_id", churchId!)
        .not("date_of_birth", "is", null);
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "birthdays_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name");
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const allowedMinistryNames = useMemo(() => {
    if (!peopleScoped) return null;
    const names = organizations
      .filter((org: any) => allowedPeopleOrgIds.includes(org.id))
      .map((org: any) => org.name)
      .filter(Boolean);
    return new Set(names);
  }, [peopleScoped, organizations, allowedPeopleOrgIds]);

  const scopedMembers = useMemo(() => {
    if (!peopleScoped || !allowedMinistryNames) return members;
    return members.filter((member: any) => {
      const ministries = getMemberMinistries(member.department);
      return ministries.some((name) => allowedMinistryNames.has(name));
    });
  }, [members, peopleScoped, allowedMinistryNames]);

  const birthdaysThisMonth = useMemo(() => {
    const ms = startOfMonth(now);
    const me = endOfMonth(now);
    return scopedMembers
      .map((m) => {
        const dob = parseISO(m.date_of_birth!);
        const bday = new Date(now.getFullYear(), dob.getMonth(), dob.getDate());
        return { ...m, birthdayDate: bday, turningAge: differenceInYears(bday, dob) };
      })
      .filter((m) => m.birthdayDate >= ms && m.birthdayDate <= me)
      .sort((a, b) => a.birthdayDate.getTime() - b.birthdayDate.getTime());
  }, [scopedMembers, now]);

  const getInitials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const isToday = (date: Date) => {
    return date.getDate() === now.getDate() && date.getMonth() === now.getMonth();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground tracking-tight">
            Birthdays This Month
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {format(now, "MMMM yyyy")} · {birthdaysThisMonth.length} birthday{birthdaysThisMonth.length !== 1 ? "s" : ""}
          </p>
        </div>
      </div>

      {birthdaysThisMonth.length === 0 ? (
        <Card className="border-border/40">
          <CardContent className="py-16 text-center">
            <Cake className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground font-medium">No birthdays this month</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {birthdaysThisMonth.map((m) => (
            <Card
              key={m.id}
              className={`group border-border/40 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${
                isToday(m.birthdayDate)
                  ? "border-l-4 border-l-accent bg-gradient-to-br from-card to-accent/[0.06]"
                  : "bg-card"
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <Avatar className="w-12 h-12 border-2 border-border">
                    <AvatarImage src={m.profile_photo_url || undefined} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                      {getInitials(m.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground truncate">{m.full_name}</p>
                      {isToday(m.birthdayDate) && (
                        <Badge className="bg-accent/15 text-accent border-accent/30 text-[10px] shrink-0">
                          🎂 Today!
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(m.birthdayDate, "EEEE, MMMM d")} · Turning {m.turningAge}
                    </p>
                    {m.department && (
                      <Badge variant="secondary" className="text-[10px] mt-2">{m.department}</Badge>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      {m.phone && (
                        <a href={`tel:${m.phone}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                          <Phone className="w-3 h-3" /> {m.phone}
                        </a>
                      )}
                      {m.email && (
                        <a href={`mailto:${m.email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BirthdaysPage;
